import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  CreditCard,
  CheckCircle,
  Clock,
  Star,
  ArrowRight,
  CircleNotch,
  Crown,
  ChartLineUp,
  ShieldCheck,
  Lightning,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const PLANS = [
  {
    id: 'monthly',
    name: 'Mensual',
    price: 395,
    originalPrice: 395,
    duration: '1 mes',
    discount: 0,
    features: [
      'Acceso al Consultor de Precios',
      'Historial de 2 años',
      'Búsqueda por categoría y estado',
      'Exportación de datos',
    ],
    popular: false,
  },
  {
    id: 'semestral',
    name: 'Semestral',
    price: 2133,
    originalPrice: 2370,
    duration: '6 meses',
    discount: 10,
    features: [
      'Todo lo del plan Mensual',
      '10% de descuento',
      'Soporte prioritario',
      'Alertas de precios',
    ],
    popular: true,
  },
  {
    id: 'annual',
    name: 'Anual',
    price: 3792,
    originalPrice: 4740,
    duration: '12 meses',
    discount: 20,
    features: [
      'Todo lo del plan Semestral',
      '20% de descuento',
      'Análisis avanzado',
      'API de consulta',
    ],
    popular: false,
  },
];

const SubscriptionsPage = () => {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    setLoading(true);
    try {
      const [statusRes, mineRes] = await Promise.all([
        subscriptionAPI.getStatus(),
        subscriptionAPI.getMine(),
      ]);
      setSubscriptionStatus(statusRes.data);
      setMySubscriptions(mineRes.data || []);
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) return;
    
    setSubscribing(true);
    try {
      await subscriptionAPI.subscribe(selectedPlan.id);
      toast.success(`¡Suscripción ${selectedPlan.name} activada!`);
      setShowConfirmDialog(false);
      setSelectedPlan(null);
      fetchSubscriptionData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la suscripción');
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      await subscriptionAPI.cancel(subscriptionId);
      toast.success('Suscripción cancelada');
      fetchSubscriptionData();
    } catch (error) {
      toast.error('Error al cancelar la suscripción');
    }
  };

  const openSubscribeDialog = (plan) => {
    setSelectedPlan(plan);
    setShowConfirmDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  const hasActiveSubscription = subscriptionStatus?.is_active;

  return (
    <div className="space-y-8" data-testid="subscriptions-page">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-amber-100 px-4 py-2 mb-4">
          <Crown className="w-5 h-5 text-amber-600" />
          <span className="text-amber-800 font-medium">Consultor de Precios</span>
        </div>
        <h1 className="text-3xl font-bold text-zinc-900 mb-4">
          Accede a datos históricos de precios de materiales
        </h1>
        <p className="text-zinc-600">
          Toma mejores decisiones de compra con acceso a más de 2 años de historial de precios
          en todo México.
        </p>
      </div>

      {/* Current Subscription Status */}
      {hasActiveSubscription && (
        <Card className="border-green-200 bg-green-50 rounded-none max-w-2xl mx-auto">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-green-800">Suscripción Activa</h3>
                <p className="text-sm text-green-700">
                  Plan {subscriptionStatus.subscription_type} • 
                  Válido hasta {new Date(subscriptionStatus.end_date).toLocaleDateString('es-MX')}
                </p>
              </div>
              <Link to="/price-consultation">
                <Button className="btn-primary rounded-none">
                  Consultar Precios
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {PLANS.map((plan) => (
          <Card 
            key={plan.id}
            className={`rounded-none relative ${
              plan.popular 
                ? 'border-amber-400 shadow-[4px_4px_0px_0px_rgba(251,191,36,1)]' 
                : 'border-zinc-200'
            }`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-amber-500 text-zinc-900 rounded-none px-4">
                  <Star className="w-3 h-3 mr-1" weight="fill" />
                  Más Popular
                </Badge>
              </div>
            )}
            
            <CardHeader className="text-center pt-8 pb-4">
              <CardTitle className="text-xl">{plan.name}</CardTitle>
              <div className="mt-4">
                {plan.discount > 0 && (
                  <p className="text-sm text-zinc-500 line-through">
                    ${plan.originalPrice.toLocaleString('es-MX')} MXN
                  </p>
                )}
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-zinc-900">
                    ${plan.price.toLocaleString('es-MX')}
                  </span>
                  <span className="text-zinc-500">MXN</span>
                </div>
                <p className="text-sm text-zinc-500 mt-1">{plan.duration}</p>
                {plan.discount > 0 && (
                  <Badge className="bg-green-100 text-green-700 rounded-none mt-2">
                    Ahorra {plan.discount}%
                  </Badge>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <Separator className="mb-6" />
              
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" weight="fill" />
                    <span className="text-zinc-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => openSubscribeDialog(plan)}
                disabled={hasActiveSubscription}
                className={`w-full rounded-none ${
                  plan.popular ? 'btn-primary' : 'btn-secondary'
                }`}
                data-testid={`subscribe-${plan.id}`}
              >
                {hasActiveSubscription ? 'Ya tienes suscripción' : 'Suscribirse'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Features Section */}
      <div className="bg-zinc-950 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-16 mt-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-12">
            ¿Qué incluye el Consultor de Precios?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: ChartLineUp,
                title: 'Historial de 2+ años',
                description: 'Accede a datos históricos de precios para analizar tendencias y estacionalidad.'
              },
              {
                icon: ShieldCheck,
                title: 'Datos verificados',
                description: 'Precios reales de transacciones completadas en la plataforma MaterMatch.'
              },
              {
                icon: Lightning,
                title: 'Búsqueda avanzada',
                description: 'Filtra por categoría, estado, material y rango de fechas.'
              },
            ].map((feature, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-amber-400 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-zinc-900" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* My Subscriptions History */}
      {mySubscriptions.length > 0 && (
        <Card className="border-zinc-200 rounded-none max-w-3xl mx-auto">
          <CardHeader className="border-b border-zinc-200">
            <CardTitle>Historial de Suscripciones</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-zinc-200">
              {mySubscriptions.map((sub) => (
                <div key={sub.subscription_id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium capitalize">{sub.type}</span>
                      <Badge 
                        variant="outline" 
                        className={`rounded-none ${
                          sub.status === 'active' ? 'bg-green-100 text-green-700' :
                          sub.status === 'queued' ? 'bg-blue-100 text-blue-700' :
                          'bg-zinc-100 text-zinc-700'
                        }`}
                      >
                        {sub.status === 'active' ? 'Activa' : 
                         sub.status === 'queued' ? 'En cola' : 
                         sub.status === 'cancelled' ? 'Cancelada' : sub.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-zinc-500 mt-1">
                      {new Date(sub.start_date).toLocaleDateString('es-MX')} - {new Date(sub.end_date).toLocaleDateString('es-MX')}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-medium">${sub.amount?.toLocaleString('es-MX')} MXN</span>
                    {sub.status === 'active' && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleCancelSubscription(sub.subscription_id)}
                        className="text-red-600 hover:text-red-700 rounded-none"
                      >
                        Cancelar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Confirmar Suscripción</DialogTitle>
            <DialogDescription>
              Estás por suscribirte al plan {selectedPlan?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPlan && (
            <div className="py-4">
              <div className="bg-zinc-100 p-4 space-y-2">
                <div className="flex justify-between">
                  <span>Plan</span>
                  <span className="font-medium">{selectedPlan.name}</span>
                </div>
                <div className="flex justify-between">
                  <span>Duración</span>
                  <span className="font-medium">{selectedPlan.duration}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-amber-600">${selectedPlan.price.toLocaleString('es-MX')} MXN</span>
                </div>
              </div>
              
              <p className="text-sm text-zinc-500 mt-4">
                <strong>Nota:</strong> En esta versión demo, la suscripción se activa inmediatamente sin pago real.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} className="rounded-none">
              Cancelar
            </Button>
            <Button 
              onClick={handleSubscribe}
              disabled={subscribing}
              className="btn-primary rounded-none"
            >
              {subscribing ? <CircleNotch className="w-5 h-5 animate-spin" /> : 'Confirmar Suscripción'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionsPage;
